import { Descriptions, Divider, Flex } from 'antd';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

const ExtraComponent = ({ data }) => {
  const intl = useIntl();
  const hasAveStrength3 = (del) =>
    !!(
      del.laboratory_note_data?.ave_strength_3 ||
      del.laboratory_note_data?.compressive_strength_3_1
    );

  const hasAveStrength7 = (del) =>
    !!(
      del.laboratory_note_data?.ave_strength_7 ||
      del.laboratory_note_data?.compressive_strength_7_1
    );

  const hasAveStrength60 = (del) =>
    !!(
      del.laboratory_note_data?.ave_strength_60 ||
      del.laboratory_note_data?.compressive_strength_60_1
    );

  const buildTable = ({ del, extraDay, compressiveKeys, aveKey }) => ({
    extraDay,
    sameDay: [
      {
        ave_strength: aveKey
          ? { [aveKey]: del.laboratory_note_data?.[aveKey] }
          : {},
        compressive_strength: compressiveKeys.map((key) => ({
          [key]:
            del.laboratory_note_data?.[key] ??
            del.laboratory_note_data?.strength_extra?.[key] ??
            null,
        })),
        docket_number: del.docket_number,
        extra_day: extraDay,
        observation: {
          observation: del.concrete_batch_data?.observation_mash || '',
        },
        reference_number: del.laboratory_note_data?.reference_number,
      },
    ],
  });

  const getTables = (del) => {
    const before3Tables = [];
    const before7Tables = [];
    const afterTables = [];

    if (hasAveStrength3(del)) {
      before3Tables.push(
        buildTable({
          del,
          extraDay: 3,
          compressiveKeys: [
            'compressive_strength_3_1',
            'compressive_strength_3_2',
            'compressive_strength_3_3',
          ],
          aveKey: 'ave_strength_3',
        }),
      );
    }

    if (hasAveStrength7(del)) {
      before7Tables.push(
        buildTable({
          del,
          extraDay: 7,
          compressiveKeys: [
            'compressive_strength_7_1',
            'compressive_strength_7_2',
            'compressive_strength_7_3',
          ],
          aveKey: 'ave_strength_7',
        }),
      );
    }

    if (hasAveStrength60(del)) {
      afterTables.push(
        buildTable({
          del,
          extraDay: 60,
          compressiveKeys: [
            'compressive_strength_60_1',
            'compressive_strength_60_2',
            'compressive_strength_60_3',
          ],
          aveKey: 'ave_strength_60',
        }),
      );
    }

    // Roturas extra dinámicas (como día 11, 15, etc)
    const strength_extra = del.laboratory_note_data?.strength_extra || {};
    const diasExtra = [
      ...new Set(
        Object.keys(strength_extra)
          .filter((k) => k.startsWith('compressive_strength_'))
          .map((k) => parseInt(k.split('_')[2], 10)),
      ),
    ];

    diasExtra.forEach((day) => {
      const compressiveKeys = [
        `compressive_strength_${day}_1`,
        `compressive_strength_${day}_2`,
        `compressive_strength_${day}_3`,
      ];

      const values = compressiveKeys.map((k) => strength_extra[k]);
      const hasAny = values.some((v) => v !== null && v !== undefined);

      if (hasAny) {
        afterTables.push(
          buildTable({
            del,
            extraDay: day,
            compressiveKeys,
            aveKey: null, // por ahora no hay medias para días extra
          }),
        );
      }
    });

    before3Tables.sort((a, b) => a.extraDay - b.extraDay);
    before7Tables.sort((a, b) => a.extraDay - b.extraDay);
    afterTables.sort((a, b) => a.extraDay - b.extraDay);

    return { before3Tables, before7Tables, afterTables };
  };

  if (!getTables(data) || getTables(data).length === 0) return null;

  const sections = ['before3Tables', 'before7Tables', 'afterTables'];

  return (
    <div style={{ marginBottom: 24 }}>
      {sections.map((key) => {
        const tables = getTables(data)[key];
        if (!tables || tables.length === 0) return null;

        return tables.map((table, i) => (
          <Descriptions
            key={i}
            bordered
            column={1}
            size="small"
            labelStyle={{ width: '150px' }}
            contentStyle={{ width: 'calc(100% - 150px)' }}
          >
            {table.sameDay.map((sameD, idx) => {
              const n = table.extraDay;
              const comps = [1, 2, 3].map((pbIdx) => {
                const key = `compressive_strength_${n}_${pbIdx}`;
                const val = sameD.compressive_strength[pbIdx - 1]?.[key];
                return (
                  <span key={pbIdx}>
                    <strong>{`Pb${pbIdx}`}</strong>
                    {`: ${val != null ? val.toFixed(2) : '-'}`}
                  </span>
                );
              });

              return (
                <Descriptions.Item
                  key={idx}
                  label={intl
                    .formatMessage(
                      { id: 'beforeNDays' },
                      { days: table.extraDay },
                    )
                    .toUpperCase()}
                >
                  <Flex>
                    {comps.map((d, index) => {
                      return (
                        <div
                          style={{
                            flex: '1 0 100px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            paddingLeft: 8,
                          }}
                          key={index}
                        >
                          {d}{' '}
                          {index !== comps.length - 1 && (
                            <Divider type="vertical" />
                          )}
                        </div>
                      );
                    })}
                  </Flex>
                </Descriptions.Item>
              );
            })}
          </Descriptions>
        ));
      })}
    </div>
  );
};
ExtraComponent.propTypes = {
  data: PropTypes.object.isRequired,
};

export default ExtraComponent;
